import {FC, RefObject, useEffect, useState} from 'react';
import {Box, Flex, Grid} from '@chakra-ui/react';
import {api, ESummaryStatus, ISummaryListItem, useT, formatRelativeTime} from '@kcast-fe/core';
import {
  QuickMenu,
  Svg,
  Switch,
  SummaryBadge,
  TBreakpointStyle,
  useScreenSize,
  ConfirmDialog
} from 'ui-kit';

export const SUMMARY_GRID_TEMPLATE: TBreakpointStyle = {
  base: '1fr 160px 180px 72px 32px',
  smToMd: '1fr 72px 32px'
};

interface IProps {
  summary: ISummaryListItem;
  onOpen: () => void;
  containerRef: RefObject<HTMLDivElement | null>;
}

const REFRESH_INTERVAL = 30 * 1000;

const SummaryListItemFC: FC<IProps> = ({summary, onOpen, containerRef}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReplay, setConfirmReplay] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [, setTick] = useState(0);

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, []);

  const stateMutation = api.summary.useUpdateSummaryStateMutation();
  const deleteMutation = api.summary.useDeleteSummaryMutation();
  const replayMutation = api.summary.useReplaySummaryMutation();
  const cancelMutation = api.summary.useCancelReplaySummaryMutation();

  const isReplaying = summary.status === ESummaryStatus.Replaying;

  const handleToggle = (enabled: boolean) => {
    stateMutation.mutate({id: summary.id, data: {enabled}});
  };

  const handleDelete = () => {
    deleteMutation.mutate(summary.id, {onSuccess: () => setConfirmDelete(false)});
  };

  const handleReplay = () => {
    replayMutation.mutate(summary.id, {onSuccess: () => setConfirmReplay(false)});
  };

  const handleCancel = () => {
    cancelMutation.mutate(summary.id, {onSuccess: () => setConfirmCancel(false)});
  };

  const isInactive = !summary.enabled;

  return (
    <>
      <Box position={'relative'}>
        <Grid
          h={'48px'}
          gap={'8px'}
          position={'relative'}
          alignItems={'center'}
          borderBottom={'thin solid'}
          borderColor={'{colors.borderDefault}'}
          _hover={{cursor: 'pointer', bg: '{colors.bgSurface}'}}
          transition={'background var(--tr-100-ei)'}
          gridTemplateColumns={SUMMARY_GRID_TEMPLATE}
          className={'group'}
          zIndex={2}
          opacity={isInactive ? 0.5 : 1}
          onClick={onOpen}
        >
          <Box className={'cut-text'} pl={'12px'} fontWeight={'500'} color={'{colors.textPrimary}'}>
            {summary.title}
          </Box>

          {!isLessThan768 && (
            <Box>
              <SummaryBadge
                status={summary.status}
                replaying_progress={summary.replaying_progress}
              />
            </Box>
          )}

          {!isLessThan768 && (
            <Box color={'{colors.textTertiary}'} className={'cut-text'}>
              {formatRelativeTime(summary.updated_at)}
            </Box>
          )}

          <Flex
            alignItems={'center'}
            justifyContent={'flex-start'}
            onClick={(e) => e.stopPropagation()}
          >
            <Switch
              checked={summary.enabled}
              onChange={handleToggle}
              disabled={stateMutation.isPending}
            />
          </Flex>

          <Box
            opacity={isLessThan768 ? 1 : 0}
            _groupHover={{opacity: 1}}
            transition={'opacity var(--tr-150-ei)'}
            onClick={(e) => e.stopPropagation()}
          >
            <QuickMenu
              hideActive
              container={containerRef}
              trigger={<Svg name={'Dots'} size={'s24'} />}
              items={[
                isReplaying
                  ? {
                      id: 'cancel',
                      name: t('summary.actions.cancelReplay'),
                      onClick: () => setConfirmCancel(true)
                    }
                  : {
                      id: 'replay',
                      name: t('summary.actions.replay'),
                      onClick: () => setConfirmReplay(true)
                    },
                {
                  id: 'delete',
                  name: t('actions.delete'),
                  isDanger: true,
                  isDisabled: isReplaying,
                  onClick: () => setConfirmDelete(true)
                }
              ]}
            />
          </Box>
        </Grid>
      </Box>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('summary.confirm.deleteTitle')}
        body={t('summary.confirm.deleteBody')}
        confirmLabel={t('actions.delete')}
        variant={'danger'}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        isOpen={confirmReplay}
        title={t('summary.confirm.replayTitle')}
        body={t('summary.confirm.replayBody')}
        confirmLabel={t('summary.actions.replay')}
        isLoading={replayMutation.isPending}
        onConfirm={handleReplay}
        onCancel={() => setConfirmReplay(false)}
      />

      <ConfirmDialog
        isOpen={confirmCancel}
        title={t('summary.confirm.cancelReplayTitle')}
        body={t('summary.confirm.cancelReplayBody')}
        confirmLabel={t('summary.actions.cancelReplay')}
        isLoading={cancelMutation.isPending}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
};

export const SummaryListItem = SummaryListItemFC;
